// Iter 82.10 / Phase E3.1 Sprint 3 — ESS My Payslips.
//
// Employee self-service surface. Lists Locked/Disbursed-parent payslips
// for the linked employee; download triggers an ESS-audit row.
import { useCallback, useEffect, useState } from "react";
import { Download, Lock, Info } from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const STATUS_TONE = {
    Generated: "bg-slate-100 text-slate-700",
    Sent: "bg-emerald-100 text-emerald-800",
    Bounced: "bg-amber-100 text-amber-800",
    OptedOut: "bg-zinc-100 text-zinc-700",
};

export default function EssMyPayslips() {
    const [items, setItems] = useState([]);
    const [note, setNote] = useState("");
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const [list, m] = await Promise.all([
                api.get("/ess/me/payslips"),
                api.get("/ess/me/payslips/meta"),
            ]);
            setItems(list.data.items || []);
            setNote(list.data.note || "");
            setMeta(m.data || {});
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load payslips"));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const download = async (ps) => {
        try {
            const r = await api.get(`/ess/me/payslips/${ps.id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Payslip_${ps.period_month_snapshot}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Downloaded — use the password formula from the hint");
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    return (
        <div className="space-y-4 max-w-4xl" data-testid="ess-payslips-page">
            <PageHeader
                eyebrow="My Payroll"
                title="Payslips"
                description="Download your monthly payslips. Each PDF is password-protected — use the formula below to open."
            />

            {note && (
                <div className="border bg-amber-50 border-amber-200 text-amber-900 text-sm p-3 rounded flex items-start gap-2" data-testid="ess-payslips-note">
                    <Info size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{note}</span>
                </div>
            )}

            <section className="border rounded-lg bg-white p-4" data-testid="ess-payslips-password-hint">
                <h2 className="text-sm font-semibold mb-1 flex items-center gap-1.5">
                    <Lock size={14} /> Password Formula
                </h2>
                <p className="text-xs text-slate-600">
                    {meta.policy_descriptions?.PAN_AADHAAR4_DDMMYYYY_V1
                        || "Contact HR for your payslip password."}
                </p>
                <p className="text-[10px] text-slate-400 mt-1.5 italic">
                    If your payslip doesn't open with this formula, your master record may be missing
                    PAN or DOB — the password footer on the PDF will tell you which policy was applied.
                </p>
            </section>

            <div className="border rounded-lg bg-white overflow-hidden">
                <table className="w-full text-sm" data-testid="ess-payslips-table">
                    <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
                        <tr>
                            <th className="px-3 py-2 text-left">Period</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-right">Generated</th>
                            <th className="px-3 py-2 text-right">Last Downloaded</th>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {loading && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">Loading…</td></tr>}
                        {!loading && items.length === 0 && (
                            <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-400" data-testid="ess-payslips-empty">No payslips yet.</td></tr>
                        )}
                        {items.map((ps) => (
                            <tr key={ps.id} className="hover:bg-slate-50" data-testid={`ess-payslip-row-${ps.id}`}>
                                <td className="px-3 py-2 font-mono">{ps.period_month_snapshot}</td>
                                <td className="px-3 py-2">
                                    <Badge className={`${STATUS_TONE[ps.status] || "bg-slate-100"}`}>{ps.status}</Badge>
                                </td>
                                <td className="px-3 py-2 text-right text-xs text-slate-500">{ps.generated_at?.slice(0, 19).replace("T", " ") || "—"}</td>
                                <td className="px-3 py-2 text-right text-xs text-slate-500">{ps.last_downloaded_at?.slice(0, 19).replace("T", " ") || "Never"}</td>
                                <td className="px-3 py-2 text-right">
                                    <Button size="sm" variant="outline" onClick={() => download(ps)} data-testid={`ess-payslip-download-${ps.id}`}>
                                        <Download size={12} className="mr-1" /> Download
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
